/**
 * @fileoverview Centralized API client for COGSLOG frontend.
 * Provides methods for all backend API endpoints.
 */

import axios from 'axios';
import { getToken, getRefreshToken, setTokens, clearTokens } from './keycloak';

/** @constant {string} API_BASE - Base URL for all API endpoints */
const API_BASE = '/api';

/** @type {string|null} Cached organization ID for request headers */
let _activeOrgId = null;

/**
 * Set the active organization ID for outgoing API requests.
 * Called by AuthContext when the user switches organizations.
 *
 * @param {string|null} orgId - The organization ID to attach to requests
 */
export function setActiveOrgId(orgId) {
  _activeOrgId = orgId;
}

/**
 * Request interceptor: attaches Bearer token and X-Organization-ID header.
 * Reads the access token from the in-memory token manager.
 */
axios.interceptors.request.use(
  (config) => {
    // Skip auth headers in test environment
    if (process.env.NODE_ENV === 'test') {
      return config;
    }

    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (_activeOrgId) {
      config.headers['X-Organization-ID'] = _activeOrgId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor: handles 401 errors by attempting token refresh.
 * Posts the refresh token to /api/auth/refresh. On success, retries the
 * original request with the new access token. On failure, clears tokens
 * and redirects to /login.
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip retry logic in test environment
    if (process.env.NODE_ENV === 'test') {
      return Promise.reject(error);
    }

    // Only handle 401 and only retry once
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          setTokens(data);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return axios(originalRequest);
        } catch {
          // Token refresh failed — clear tokens and redirect to login
          clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * API service object containing all HTTP request methods.
 * @namespace api
 */
const api = {
  // ==================== Businesses ====================

  /**
   * Get all businesses.
   * @returns {Promise<{data: Array}>} List of businesses
   */
  getBusinesses: (params) => axios.get(`${API_BASE}/businesses`, { params }),

  /**
   * Get a single business by ID.
   * @param {number} id - Business ID
   * @returns {Promise<{data: Object}>} Business data
   */
  getBusiness: (id) => axios.get(`${API_BASE}/businesses/${id}`),

  /**
   * Create a new business.
   * @param {Object} data - Business data (name, address, tax_id, etc.)
   * @returns {Promise<{data: Object}>} Created business with ID
   */
  createBusiness: (data) => axios.post(`${API_BASE}/businesses`, data),

  /**
   * Update an existing business.
   * @param {number} id - Business ID
   * @param {Object} data - Updated business data
   * @returns {Promise} Empty response on success
   */
  updateBusiness: (id, data) => axios.put(`${API_BASE}/businesses/${id}`, data),

  /**
   * Delete a business.
   * @param {number} id - Business ID
   * @returns {Promise} Empty response on success
   */
  deleteBusiness: (id) => axios.delete(`${API_BASE}/businesses/${id}`),

  /**
   * Toggle business active/inactive status.
   * @param {number} id - Business ID
   * @returns {Promise<{data: {status: string}}>} New status
   */
  toggleBusinessStatus: (id) => axios.post(`${API_BASE}/businesses/${id}/toggle-status`),

  /**
   * Get child businesses for a parent business.
   * @param {number} businessId - The parent business ID
   * @returns {Promise<{data: Array}>} List of child businesses
   */
  getBusinessChildren: (businessId) => axios.get(`${API_BASE}/businesses/${businessId}/children`),

  // ==================== Vendors ====================

  /**
   * Get all vendors.
   * @returns {Promise<{data: Array}>} List of vendors
   */
  getVendors: (params) => axios.get(`${API_BASE}/vendors`, { params }),

  /**
   * Get a single vendor by ID.
   * @param {number} id - Vendor ID
   * @returns {Promise<{data: Object}>} Vendor data
   */
  getVendor: (id) => axios.get(`${API_BASE}/vendors/${id}`),

  /**
   * Create a new vendor.
   * @param {Object} data - Vendor data (name, contact_info, etc.)
   * @returns {Promise<{data: Object}>} Created vendor with ID
   */
  createVendor: (data) => axios.post(`${API_BASE}/vendors`, data),

  /**
   * Update an existing vendor.
   * @param {number} id - Vendor ID
   * @param {Object} data - Updated vendor data
   * @returns {Promise} Empty response on success
   */
  updateVendor: (id, data) => axios.put(`${API_BASE}/vendors/${id}`, data),

  /**
   * Delete a vendor.
   * @param {number} id - Vendor ID
   * @returns {Promise} Empty response on success
   */
  deleteVendor: (id) => axios.delete(`${API_BASE}/vendors/${id}`),

  /**
   * Toggle vendor active/inactive status.
   * @param {number} id - Vendor ID
   * @returns {Promise<{data: {status: string}}>} New status
   */
  toggleVendorStatus: (id) => axios.post(`${API_BASE}/vendors/${id}/toggle-status`),

  /**
   * Get vendor logo image URL.
   * @param {number} id - Vendor ID
   * @returns {string} URL to the vendor logo image
   */
  getVendorLogoUrl: (id) => `${API_BASE}/vendors/${id}/logo`,

  /**
   * Get vendor logo image as a blob (with auth).
   * @param {number} id - Vendor ID
   * @returns {Promise<{data: Blob}>} PNG image blob
   */
  getVendorLogo: (id) => axios.get(`${API_BASE}/vendors/${id}/logo`, { responseType: 'blob' }),

  /**
   * Delete vendor logo (hash and image file).
   * @param {number} id - Vendor ID
   * @returns {Promise} 204 on success
   */
  deleteVendorLogo: (id) => axios.delete(`${API_BASE}/vendors/${id}/logo`),

  // ==================== Categories ====================

  /**
   * Get all categories.
   * @returns {Promise<{data: Array}>} List of categories (3-level hierarchy)
   */
  getCategories: (params) => axios.get(`${API_BASE}/categories`, { params }),

  /**
   * Get level-3 categories with full path.
   * @returns {Promise<{data: Array}>} Categories with full_path field
   */
  getLevel3Categories: () => axios.get(`${API_BASE}/categories/level3-list`),

  /**
   * Get a single category by ID.
   * @param {number} id - Category ID
   * @returns {Promise<{data: Object}>} Category data
   */
  getCategory: (id) => axios.get(`${API_BASE}/categories/${id}`),

  /**
   * Create a new category.
   * @param {Object} data - Category data (level1, level2?, level3?)
   * @returns {Promise<{data: Object}>} Created category with ID
   */
  createCategory: (data) => axios.post(`${API_BASE}/categories`, data),

  /**
   * Update an existing category.
   * @param {number} id - Category ID
   * @param {Object} data - Updated category data
   * @returns {Promise} Empty response on success
   */
  updateCategory: (id, data) => axios.put(`${API_BASE}/categories/${id}`, data),

  /**
   * Delete a category.
   * @param {number} id - Category ID
   * @returns {Promise} Empty response on success
   */
  deleteCategory: (id) => axios.delete(`${API_BASE}/categories/${id}`),

  /**
   * Toggle category active/inactive status.
   * @param {number} id - Category ID
   * @returns {Promise<{data: {status: string}}>} New status
   */
  toggleCategoryStatus: (id) => axios.post(`${API_BASE}/categories/${id}/toggle-status`),
  
  // ==================== SKU Catalog ====================

  /**
   * Get SKU catalog with optional filters.
   * @param {Object} [params] - Filter parameters (vendor_id?, sku?)
   * @returns {Promise<{data: Array}>} List of SKUs with vendor/category info
   */
  getSKUCatalog: (params) => axios.get(`${API_BASE}/sku-catalog`, { params }),

  /**
   * Get a single SKU by ID.
   * @param {number} id - SKU ID
   * @returns {Promise<{data: Object}>} SKU data
   */
  getSKU: (id) => axios.get(`${API_BASE}/sku-catalog/${id}`),

  /**
   * Create a new SKU.
   * @param {Object} data - SKU data (vendor_id, sku, item_name, etc.)
   * @returns {Promise<{data: Object}>} Created SKU with ID
   */
  createSKU: (data) => axios.post(`${API_BASE}/sku-catalog`, data),

  /**
   * Update an existing SKU.
   * @param {number} id - SKU ID
   * @param {Object} data - Updated SKU data
   * @returns {Promise} Empty response on success
   */
  updateSKU: (id, data) => axios.put(`${API_BASE}/sku-catalog/${id}`, data),

  /**
   * Set pack size on a SKU by (vendor_id, sku) natural key. Used by the
   * inline UOM-fix form on the post-save pack-size warning rows so callers
   * don't need an extra lookup to find the SKU's database id.
   *
   * @param {Object} data - { vendor_id, sku, base_unit, base_units_per_pack }
   * @returns {Promise} { id, sku, base_unit, base_units_per_pack }
   */
  setSKUPack: (data) => axios.post(`${API_BASE}/sku-catalog/set-pack`, data),

  /**
   * Delete a SKU.
   * @param {number} id - SKU ID
   * @returns {Promise} Empty response on success
   */
  deleteSKU: (id) => axios.delete(`${API_BASE}/sku-catalog/${id}`),

  /**
   * Get category suggestions for uncategorized SKUs based on invoice history.
   * @returns {Promise<{data: Array}>} List of suggestions with sku_id, category_path, confidence
   */
  suggestSKUCategories: () => axios.get(`${API_BASE}/sku-catalog/suggest-categories`),

  /**
   * Bulk update categories for multiple SKU catalog items.
   * @param {Array<{sku_id: number, category_id: number}>} updates - Array of SKU/category pairs
   * @returns {Promise<{data: {updated: number}}>} Number of rows updated
   */
  bulkUpdateSKUCategories: (updates) =>
    axios.post(`${API_BASE}/sku-catalog/bulk-update-categories`, { updates }),

  // ==================== Invoices ====================

  /**
   * Get invoices with optional filters.
   * @param {Object} [params] - Filter params (business_id?, vendor_id?, start_date?, end_date?)
   * @returns {Promise<{data: Array}>} List of invoices with vendor/business names
   */
  getInvoices: (params) => axios.get(`${API_BASE}/invoices`, { params }),

  /**
   * Get a single invoice with line items and misc charges.
   * @param {number} id - Invoice ID
   * @returns {Promise<{data: Object}>} Full invoice data
   */
  getInvoice: (id) => axios.get(`${API_BASE}/invoices/${id}`),

  /**
   * Create a new invoice with line items.
   * @param {Object} data - Invoice data with line_items and misc_charges arrays
   * @returns {Promise<{data: {id: number, total: number}}>} Created invoice info
   */
  createInvoice: (data) => axios.post(`${API_BASE}/invoices`, data),

  /**
   * Update an existing invoice.
   * @param {number} id - Invoice ID
   * @param {Object} data - Updated invoice data (replaces line items)
   * @returns {Promise} Empty response on success
   */
  updateInvoice: (id, data) => axios.put(`${API_BASE}/invoices/${id}`, data),

  /**
   * Delete an invoice (cascades to line items).
   * @param {number} id - Invoice ID
   * @returns {Promise} Empty response on success
   */
  deleteInvoice: (id) => axios.delete(`${API_BASE}/invoices/${id}`),

  /**
   * Append line items to an existing invoice (multi-page invoice support).
   * @param {number} id - Invoice ID to append to
   * @param {Object} data - { line_items, misc_charges, pdf_filename }
   * @returns {Promise<{data: {id: number, total: number, line_items_added: number}}>}
   */
  appendToInvoice: (id, data) => axios.post(`${API_BASE}/invoices/${id}/append`, data),

  /**
   * Toggle invoice active/inactive status.
   * @param {number} id - Invoice ID
   * @returns {Promise<{data: {status: string}}>} New status
   */
  toggleInvoiceStatus: (id) => axios.post(`${API_BASE}/invoices/${id}/toggle-status`),
  
  // ==================== Analytics ====================

  /**
   * Get analytics summary (total spend, invoice count, etc.).
   * @param {Object} [params] - Filter params (business_id?, vendor_id?, dates?)
   * @returns {Promise<{data: Object}>} Summary statistics
   */
  getAnalyticsSummary: (params) => axios.get(`${API_BASE}/analytics/summary`, { params }),

  /**
   * Get spending breakdown by vendor.
   * @param {Object} [params] - Filter params (business_id?, dates?)
   * @returns {Promise<{data: Array}>} Vendor spending data
   */
  getAnalyticsByVendor: (params) => axios.get(`${API_BASE}/analytics/by-vendor`, { params }),

  /**
   * Get spending breakdown by category.
   * @param {Object} [params] - Filter params including level (1-3)
   * @returns {Promise<{data: Array}>} Category spending data
   */
  getAnalyticsByCategory: (params) => axios.get(`${API_BASE}/analytics/by-category`, { params }),

  /**
   * Get spending trends with configurable granularity.
   * @param {Object} [params] - Filter params (business_id?, vendor_id?, granularity?: 'daily'|'weekly'|'monthly', start_date?, end_date?)
   * @returns {Promise<{data: Array}>} Trend data points
   */
  getAnalyticsTrends: (params) => axios.get(`${API_BASE}/analytics/trends`, { params }),

  /**
   * Get vendor performance metrics (avg invoice, frequency, cost trend, spend share).
   * @param {Object} [params] - Filter params (business_id?, vendor_id?)
   * @returns {Promise<{data: Array}>} Vendor performance data
   */
  getVendorPerformance: (params) => axios.get(`${API_BASE}/analytics/vendor-performance`, { params }),

  /**
   * Get misc charges analytics grouped by description and vendor.
   * @param {Object} [params] - Filter params (business_id?, start_date?, end_date?)
   * @returns {Promise<{data: Array}>} Misc charges breakdown
   */
  getMiscChargesAnalytics: (params) => axios.get(`${API_BASE}/analytics/misc-charges`, { params }),

  /**
   * Get budget vs actual comparison data.
   * @param {Object} [params] - Filter params (business_id?, period?, start_date?, end_date?)
   * @returns {Promise<{data: Array}>} Budget vs actual comparison data
   */
  getBudgetVsActual: (params) => axios.get(`${API_BASE}/analytics/budget-vs-actual`, { params }),

  // ==================== Budgets ====================

  /**
   * Get all budgets.
   * @param {Object} [params] - Filter params (business_id?)
   * @returns {Promise<{data: Array}>} List of budgets
   */
  getBudgets: (params) => axios.get(`${API_BASE}/budgets`, { params }),

  /**
   * Create a new budget.
   * @param {Object} data - Budget data (business_id, category_id?, vendor_id?, period, amount, start_date, end_date)
   * @returns {Promise<{data: Object}>} Created budget with ID
   */
  createBudget: (data) => axios.post(`${API_BASE}/budgets`, data),

  /**
   * Update an existing budget.
   * @param {number} id - Budget ID
   * @param {Object} data - Updated budget data
   * @returns {Promise} Empty response on success
   */
  updateBudget: (id, data) => axios.put(`${API_BASE}/budgets/${id}`, data),

  /**
   * Delete a budget.
   * @param {number} id - Budget ID
   * @returns {Promise} Empty response on success
   */
  deleteBudget: (id) => axios.delete(`${API_BASE}/budgets/${id}`),
  
  // ==================== OCR Queue ====================

  /**
   * Get all items in the OCR processing queue.
   * @param {Object} [params] - Filter params (status?: 'pending'|'processing'|'completed'|'error'|'skipped')
   * @returns {Promise<{data: Array}>} List of queue items with vendor names
   */
  getOCRQueue: (params) => axios.get(`${API_BASE}/ocr/queue`, { params }),

  /**
   * Upload multiple PDFs to the processing queue (batch upload).
   * @param {FormData} formData - Form data with 'files' field containing PDFs
   * @returns {Promise<{data: Array}>} List of created queue items with detected vendors
   */
  uploadToQueue: (formData) => axios.post(`${API_BASE}/ocr/queue`, formData),

  /**
   * Get a single queue item with full details.
   * @param {number} id - Queue item ID
   * @returns {Promise<{data: Object}>} Queue item with extracted_data parsed
   */
  getQueueItem: (id) => axios.get(`${API_BASE}/ocr/queue/${id}`),

  /**
   * Update a queue item (reorder, change status, update vendor).
   * @param {number} id - Queue item ID
   * @param {Object} data - Fields to update (position_in_queue?, processing_status?, detected_vendor_id?)
   * @returns {Promise<{data: Object}>} Updated queue item
   */
  updateQueueItem: (id, data) => axios.put(`${API_BASE}/ocr/queue/${id}`, data),

  /**
   * Delete a queue item and its associated PDF file.
   * @param {number} id - Queue item ID
   * @returns {Promise} Empty response on success
   */
  deleteQueueItem: (id) => axios.delete(`${API_BASE}/ocr/queue/${id}`),

  /**
   * Scan an S3 prefix for PDF files and add them to the queue.
   *
   * The backend lists ``S3_PDF_BUCKET`` under ``{org_id}/{prefix}`` and
   * enqueues every matching ``.pdf`` object into ``{org_id}/queue/``.
   * ``org_id`` is auto-populated from the active org unless the caller
   * passes it explicitly (super-admin cross-org scans).
   *
   * @param {Object} data - {prefix: string, org_id?: number}
   * @returns {Promise<{data: Array}>} List of created queue items
   */
  scanDirectoryToQueue: (data) => {
    const payload = { ...data };
    if (payload.org_id == null && _activeOrgId != null) {
      payload.org_id = _activeOrgId;
    }
    return axios.post(`${API_BASE}/ocr/queue/scan-directory`, payload);
  },

  /**
   * Get the next unverified item in the queue for processing.
   * @returns {Promise<{data: Object}>} Next queue item needing attention
   */
  getNextQueueItem: () => axios.get(`${API_BASE}/ocr/queue/next`),

  /**
   * Mark a queue item as verified with optional corrections.
   * @param {number} id - Queue item ID
   * @param {Object} [data] - Optional {corrections: Object} with field corrections
   * @returns {Promise<{data: Object}>} Updated queue item
   */
  verifyQueueItem: (id, data) => axios.put(`${API_BASE}/ocr/queue/${id}/verify`, data),

  /**
   * Create an invoice from a verified queue item.
   * @param {number} id - Queue item ID
   * @param {Object} data - {business_id, vendor_id?, invoice_data?, line_items?}
   * @returns {Promise<{data: {id: number, total: number, queue_id: number}}>} Created invoice
   */
  createInvoiceFromQueue: (id, data) => axios.post(`${API_BASE}/ocr/queue/${id}/create-invoice`, data),

  // ==================== COGSLOG OCR ====================

  /**
   * Upload a PDF for COGSLOG OCR processing.
   * @param {File} file - PDF file to upload
   * @returns {Promise<{data: {filepath, filename, preview_image}}>}
   */
  uploadForVision: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_BASE}/ocr/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Identify vendor from a PDF using COGSLOG.
   * @param {string} filepath - Server path to the PDF
   * @returns {Promise<{data: {vendor_id, vendor_name, matched_name, confidence}}>}
   */
  identifyVendor: (filepath) => axios.post(`${API_BASE}/ocr/identify-vendor`, { filepath }),

  /**
   * Extract all invoice data from a PDF using COGSLOG.
   * @param {Object} data - {filepath, vendor_id?, vendor_name?}
   * @returns {Promise<{data: Object}>} Structured invoice data with line items and categories
   */
  extractWithVision: (data) => axios.post(`${API_BASE}/ocr/extract-with-vision`, data),

  /**
   * Process a queue item using COGSLOG.
   * @param {number} queueId - Queue item ID
   * @param {Object} [data] - Optional {vendor_id} override
   * @returns {Promise<{data: {id, processing_status, extracted_data}}>}
   */
  processQueueItemWithVision: (queueId, data) =>
    axios.post(`${API_BASE}/ocr/queue/${queueId}/process-vision`, data),

  /**
   * Rotate the stored PDF for a queue item 90° in the given direction.
   * Re-extraction is NOT triggered server-side; the caller should chain
   * `processQueueItemWithVision` after this resolves to re-run extraction
   * against the corrected orientation.
   *
   * @param {number} queueId - Queue item ID
   * @param {'left'|'right'} direction - Rotation direction (CCW vs CW)
   * @returns {Promise<{data: {id, processing_status, preview_image, rotated}}>}
   */
  rotateQueueItem: (queueId, direction) =>
    axios.post(`${API_BASE}/ocr/queue/${queueId}/rotate`, { direction }),

  /**
   * Get a preview image for a queue item's PDF (generated on demand).
   *
   * @param {number} queueId - Queue item ID
   * @returns {Promise<{data: {preview_image: string|null}}>} Base64 PNG or null
   */
  getQueueItemPreview: (queueId) =>
    axios.get(`${API_BASE}/ocr/queue/${queueId}/preview`),

  /**
   * Process multiple queue items with COGSLOG.
   * @param {number[]} queueIds - Array of queue item IDs
   * @returns {Promise<{data: {results: Array}}>}
   */
  processQueueBatchWithVision: (queueIds) =>
    axios.post(`${API_BASE}/ocr/queue/process-batch`, { queue_ids: queueIds }),

  // ==================== LLM Analytics ====================

  /**
   * Ask a natural language analytics question powered by COGSLOG.
   * @param {string} question - The question to ask
   * @param {number} [businessId] - Optional business ID filter
   * @returns {Promise<{data: {answer, data_points, chart_suggestion, tokens_used}}>}
   */
  askAnalyticsQuestion: (question, businessId) =>
    axios.post(`${API_BASE}/analytics/ask`, { question, business_id: businessId }),

  /**
   * Get price history for vendor/SKU combinations.
   * @param {Object} params - {vendor_id?, sku?, item_name?, start_date?, end_date?}
   * @returns {Promise<{data: Array}>}
   */
  getPriceHistory: (params) => axios.get(`${API_BASE}/analytics/price-history`, { params }),

  /**
   * Get significant price changes.
   * @param {Object} params - {threshold?, days?}
   * @returns {Promise<{data: Array}>}
   */
  getPriceChanges: (params) => axios.get(`${API_BASE}/analytics/price-changes`, { params }),

  /**
   * Get cross-vendor price comparison.
   * @param {Object} params - {item_name?, sku?}
   * @returns {Promise<{data: Array}>}
   */
  getVendorComparison: (params) => axios.get(`${API_BASE}/analytics/vendor-comparison`, { params }),

  // ==================== Line Item Categories ====================

  /**
   * Sync uncategorized line items with SKU catalog categories.
   * Matches by SKU code + vendor, only fills NULL/0 categories.
   * @returns {Promise<{data: {updated: number, total_uncategorized_before: number, total_uncategorized_after: number}}>}
   */
  syncLineItemCategories: () => axios.post(`${API_BASE}/line-items/sync-categories`),

  /**
   * Update the category of a single line item.
   * @param {number} id - Line item ID
   * @param {Object} data - {category_id: number|null}
   * @returns {Promise<{data: {id: number, category_id: number|null}}>}
   */
  updateLineItemCategory: (id, data) => axios.patch(`${API_BASE}/line-items/${id}/category`, data),

  // ==================== Export ====================

  /**
   * Download invoice data as Excel file via axios blob response.
   * Creates a temporary download link to trigger the browser file save dialog.
   *
   * @param {Object} [params] - Filter params (business_id?, vendor_id?, dates?)
   * @returns {Promise<void>}
   */
  exportExcel: async (params) => {
    let url = null;
    try {
      const response = await axios.get(`${API_BASE}/export/excel`, { params, responseType: 'blob' });
      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cogs_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  },

  /**
   * Export data in multiple formats (xlsx, csv, json).
   * For xlsx and csv, triggers browser download. For json, returns parsed data.
   *
   * @param {Object} [params] - Filter params (business_id?, vendor_id?, dates?)
   * @param {string} [format='xlsx'] - Export format: 'xlsx', 'csv', or 'json'
   * @returns {Promise<void>}
   */
  exportData: async (params, format = 'xlsx') => {
    const allParams = { ...params, format };
    let url = null;
    try {
      if (format === 'json') {
        const response = await axios.get(`${API_BASE}/export/data`, { params: allParams });
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'cogs_export.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const ext = format === 'csv' ? 'csv' : 'xlsx';
        const response = await axios.get(`${API_BASE}/export/data`, { params: allParams, responseType: 'blob' });
        url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `cogs_export.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  },

  // ==================== Organization Management ====================

  /**
   * List organizations the current user belongs to.
   *
   * @returns {Promise<Object>} Axios response with array of org objects
   */
  getOrganizations: () => axios.get(`${API_BASE}/organizations`),

  /**
   * Create a new organization.
   *
   * @param {Object} data - Organization data {name: string, slug?: string}
   * @returns {Promise<Object>} Axios response with created org
   */
  createOrganization: (data) => axios.post(`${API_BASE}/organizations`, data),

  /**
   * Get organization details.
   *
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Axios response with org details
   */
  getOrganization: (orgId) => axios.get(`${API_BASE}/organizations/${orgId}`),

  /**
   * Update an organization.
   *
   * @param {number} orgId - Organization ID
   * @param {Object} data - Fields to update {name?: string, slug?: string}
   * @returns {Promise<Object>} Axios response with updated org
   */
  updateOrganization: (orgId, data) => axios.put(`${API_BASE}/organizations/${orgId}`, data),

  /**
   * List members of an organization.
   *
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Axios response with array of member objects
   */
  getOrgMembers: (orgId) => axios.get(`${API_BASE}/organizations/${orgId}/members`),

  /**
   * Invite a member to an organization.
   *
   * @param {number} orgId - Organization ID
   * @param {Object} data - Invite data {email: string, role?: string}
   * @returns {Promise<Object>} Axios response with new membership
   */
  inviteOrgMember: (orgId, data) => axios.post(`${API_BASE}/organizations/${orgId}/members`, data),

  /**
   * Update a member's role in an organization.
   *
   * @param {number} orgId - Organization ID
   * @param {number} memberId - Membership record ID
   * @param {Object} data - Role update {role: string}
   * @returns {Promise<Object>} Axios response with updated membership
   */
  updateOrgMember: (orgId, memberId, data) =>
    axios.put(`${API_BASE}/organizations/${orgId}/members/${memberId}`, data),

  /**
   * Remove a member from an organization.
   *
   * @param {number} orgId - Organization ID
   * @param {number} memberId - Membership record ID
   * @returns {Promise<Object>} Axios response (204 No Content)
   */
  removeOrgMember: (orgId, memberId) =>
    axios.delete(`${API_BASE}/organizations/${orgId}/members/${memberId}`),

  // ==================== Super Admin ====================

  /**
   * List all organizations (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix (e.g., '/api/{secret}/admin')
   * @returns {Promise<Object>} Axios response with all orgs
   */
  adminListOrgs: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/organizations`),

  /**
   * Delete an organization (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @param {number} orgId - Organization ID to delete
   * @returns {Promise<Object>} Axios response (204 No Content)
   */
  adminDeleteOrg: (adminApiPrefix, orgId) =>
    axios.delete(`${adminApiPrefix}/organizations/${orgId}`),

  /**
   * List all users (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @returns {Promise<Object>} Axios response with all users
   */
  adminListUsers: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/users`),

  /**
   * Update a user's super-admin status (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @param {number} userId - The user id to update
   * @param {{is_super_admin: boolean}} data - Body
   * @returns {Promise<Object>} Axios response with the updated user row
   */
  adminUpdateUser: (adminApiPrefix, userId, data) =>
    axios.patch(`${adminApiPrefix}/users/${userId}`, data),

  /**
   * List businesses for a specific org (super admin only).
   * Powers the SuperAdmin → Invoices tab business picker.
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @param {number} orgId - Organization id
   * @returns {Promise<Object>} Axios response with array of business rows
   */
  adminListBusinessesForOrg: (adminApiPrefix, orgId) =>
    axios.get(`${adminApiPrefix}/orgs/${orgId}/businesses`),

  /**
   * Single-call SuperAdmin dashboard payload (counts + recent activity lists).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @returns {Promise<Object>} Axios response with `{counts, newly_registered, recently_active, recent_invoices}`
   */
  adminGetDashboard: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/dashboard`),

  /**
   * List invoices for a business (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @param {number} orgId - Organization id (must match the business)
   * @param {number} businessId - Business id
   * @param {{page?: number, per_page?: number}} [params]
   * @returns {Promise<Object>} Axios response with `{items, total, page, per_page, business}`
   */
  adminListInvoicesForBusiness: (adminApiPrefix, orgId, businessId, params = {}) =>
    axios.get(
      `${adminApiPrefix}/orgs/${orgId}/businesses/${businessId}/invoices`,
      { params }
    ),

  /**
   * Bulk-download invoice PDFs as a ZIP (super admin only).
   * Returns an Axios response with the ZIP blob — caller wires it up to
   * a hidden anchor + click for the download trigger.
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @param {number} orgId - Organization id
   * @param {number[]} invoiceIds - Invoice ids to include (max 100)
   * @returns {Promise<Object>} Axios response with `responseType=blob`
   */
  adminDownloadInvoicesZip: (adminApiPrefix, orgId, invoiceIds) =>
    axios.post(
      `${adminApiPrefix}/orgs/${orgId}/invoices/zip`,
      { invoice_ids: invoiceIds },
      { responseType: 'blob' }
    ),

  /**
   * Get system configuration (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @returns {Promise<Object>} Axios response with system config
   */
  adminGetConfig: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/system-config`),

  /**
   * Update system configuration (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @param {Object} data - Config key-value pairs to update
   * @returns {Promise<Object>} Axios response with update confirmation
   */
  adminUpdateConfig: (adminApiPrefix, data) =>
    axios.put(`${adminApiPrefix}/system-config`, data),

  /**
   * Get billing overview (super admin only).
   *
   * @param {string} adminApiPrefix - Full API prefix
   * @returns {Promise<Object>} Axios response with billing data
   */
  adminGetBilling: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/billing`),

  /**
   * List rows from a cross-org Data Browser endpoint (super admin only).
   *
   * Backend endpoint: GET /api/{secret}/admin/data/{entity} — implemented in
   * Phase 11 Wave 3 (routes/admin.py). Powers the SuperAdmin → Data sidebar
   * sub-pages via the AdminDataBrowser component shell.
   *
   * @param {string} adminApiPrefix - Full admin API prefix (e.g. '/api/{secret}/admin')
   * @param {string} entity - One of: businesses, vendors, invoices, line-items,
   *   categories, sku-catalog, misc-charges, price-history
   * @param {Object} [params={}] - Query parameters: page, per_page, search, sort
   * @returns {Promise<{data: {items: Array, total: number, page: number, per_page: number}}>}
   * @example
   * const { data } = await api.adminDataList('/api/abc123/admin', 'vendors',
   *   { page: 1, per_page: 50, search: 'Sysco' });
   */
  adminDataList: (adminApiPrefix, entity, params = {}) =>
    axios.get(`${adminApiPrefix}/data/${entity}`, { params }),

  /**
   * Download an xlsx export of any /admin/data/<entity> listing.
   * Hits the same endpoint with `?format=xlsx`; backend bypasses pagination
   * (capped at 10,000 rows) and returns an xlsx blob. Triggers a browser
   * download via a temporary anchor.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {string} entity - Same entities as adminDataList.
   * @param {Object} [params={}] - search, sort, sort_dir.
   * @returns {Promise<void>} Resolves once the download is initiated.
   */
  adminDataExport: async (adminApiPrefix, entity, params = {}) => {
    const response = await axios.get(`${adminApiPrefix}/data/${entity}`, {
      params: { ...params, format: 'xlsx' },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.download = `${entity}-${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // ==================== Super Admin — Email Ingest (v3.0 Phase 3) ====================

  /**
   * List authorized-sender bindings across every tenant (super admin only).
   *
   * Backend endpoint: GET /api/{secret}/admin/email-ingest/verified —
   * implemented in routes/admin_email_ingest.py. Filters out soft-deleted
   * rows, sorted by `created_at DESC`. Under the v3.0 Phase 3.5
   * proactive-add model, rows may be `verification_status='pending'` or
   * `'verified'`; both show here.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok: boolean, rows: Array<{
   *   id: number, email: string, business_id: number|null,
   *   business_name: string|null, org_id: number|null,
   *   org_name: string|null,
   *   verification_status: 'pending'|'verified',
   *   verified_at: string|null,
   *   verification_expires_at: string|null,
   *   added_at: string, added_by_display_name: string|null
   * }>}}>}
   */
  adminEmailIngestListVerified: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/email-ingest/verified`),

  /**
   * Revoke an authorized-sender binding (soft-delete via
   * `deleted_at = NOW()`). Idempotent: 404 if the row is already
   * revoked or nonexistent.
   *
   * Backend endpoint: DELETE /api/{secret}/admin/email-ingest/verified/<id>.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} id - The email_ingest_senders row id.
   * @returns {Promise<{data: {ok: boolean}}>}
   */
  adminEmailIngestRevokeVerified: (adminApiPrefix, id) =>
    axios.delete(`${adminApiPrefix}/email-ingest/verified/${id}`),

  // ==================== Org — Authorized Invoice Email Senders ====================
  // v3.0 Phase 3.5 (2026-08-29) — proactive-add authorization model.
  // Org admin/editor manages authorized senders in Org Settings; the
  // sender receives a verify-link mail and clicks through to
  // /verify-invoice-email to finalize.

  /**
   * List authorized-sender bindings on an org.
   *
   * Backend: GET /api/organizations/<org_id>/email-ingest-senders
   *
   * @param {number} orgId - Target organization id.
   * @returns {Promise<{data: {ok: boolean, rows: Array<{
   *   id: number, email: string, business_id: number|null,
   *   business_name: string|null,
   *   verification_status: 'pending'|'verified'|'expired',
   *   verified_at: string|null, verification_expires_at: string|null,
   *   added_at: string, added_by_display_name: string|null
   * }>}}>}
   */
  listOrgEmailIngestSenders: (orgId) =>
    axios.get(`/api/organizations/${orgId}/email-ingest-senders`),

  /**
   * Add a new authorized-sender binding on an org. Fires the verify
   * email as a side effect (server-side, best-effort).
   *
   * Backend: POST /api/organizations/<org_id>/email-ingest-senders
   *
   * @param {number} orgId - Target organization id.
   * @param {{email: string, business_id?: number|null}} body - Sender
   *   email + optional business_id (auto-defaults when the org has
   *   exactly one active business).
   * @returns {Promise<Object>} Axios response with the created row.
   */
  addOrgEmailIngestSender: (orgId, body) =>
    axios.post(`/api/organizations/${orgId}/email-ingest-senders`, body),

  /**
   * Soft-delete an authorized-sender binding.
   *
   * Backend: DELETE /api/organizations/<org_id>/email-ingest-senders/<id>
   */
  removeOrgEmailIngestSender: (orgId, id) =>
    axios.delete(`/api/organizations/${orgId}/email-ingest-senders/${id}`),

  /**
   * Regenerate the verify token + resend the verify email for a
   * pending binding. Server 400s on already-verified rows.
   *
   * Backend: POST /api/organizations/<org_id>/email-ingest-senders/<id>/resend-verification
   */
  resendOrgEmailIngestVerification: (orgId, id) =>
    axios.post(
      `/api/organizations/${orgId}/email-ingest-senders/${id}/resend-verification`,
    ),

  // ==================== Public — Verify Invoice Email ====================
  // Public (no auth) — the sender clicks a link from their inbox.

  /**
   * Look up the verify-page metadata (email, org name, expiry) for a
   * token. Public — no auth headers required.
   *
   * Backend: GET /api/email-verify/<token>
   */
  getEmailVerify: (token) =>
    axios.get(`/api/email-verify/${encodeURIComponent(token)}`),

  /**
   * Confirm ownership of the email — flips the sender row to verified.
   * Idempotent 200 on an already-verified row.
   *
   * Backend: POST /api/email-verify/<token>
   */
  postEmailVerify: (token) =>
    axios.post(`/api/email-verify/${encodeURIComponent(token)}`),

  // ==================== Super Admin — Analytics (PR A3/A4) ====================

  /**
   * "Right now" snapshot — active sessions and top tallies for the given window.
   *
   * @param {string} adminApiPrefix - Full admin API prefix (e.g., '/api/{secret}/admin')
   * @param {number} [minutes=15] - Window size in minutes. Server clamps to [5, 60].
   * @returns {Promise<Object>} Axios response with {ok, data}.
   */
  adminAnalyticsNow: (adminApiPrefix, minutes = 15) =>
    axios.get(`${adminApiPrefix}/analytics/now`, { params: { minutes } }),

  /**
   * Daily trends series + totals for the given window.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [days=30] - Window size in days. Server clamps to [1, 365].
   * @returns {Promise<Object>} Axios response with {ok, data}.
   */
  adminAnalyticsTrends: (adminApiPrefix, days = 30) =>
    axios.get(`${adminApiPrefix}/analytics/trends`, { params: { days } }),

  /**
   * List identified users with recent activity.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [days=30] - Window size in days. Server clamps to [1, 90].
   * @param {number} [limit=50] - Max rows. Server clamps to [1, 200].
   * @returns {Promise<Object>} Axios response with {ok, data}.
   */
  adminAnalyticsPeople: (adminApiPrefix, days = 30, limit = 50) =>
    axios.get(`${adminApiPrefix}/analytics/people`, { params: { days, limit } }),

  /**
   * Detail for one user — profile + traits + recent sessions/events.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} userId - The user id.
   * @returns {Promise<Object>} Axios response with {ok, data}. 404 if deactivated.
   */
  adminAnalyticsPerson: (adminApiPrefix, userId) =>
    axios.get(`${adminApiPrefix}/analytics/people/${userId}`),

  /**
   * List visit sessions for a window — anonymous visitors included.
   *
   * This is the counterpart to `adminAnalyticsPeople`: People only covers
   * signed-in users, so early-stage traffic (which is mostly signed out)
   * is invisible there.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [days=30] - Window size in days. Server clamps to [1, 90].
   * @param {number} [limit=100] - Max rows. Server clamps to [1, 500].
   * @param {'all'|'yes'|'no'} [identified='all'] - Filter by whether the
   *   session resolved to a signed-in user.
   * @returns {Promise<Object>} Axios response with {ok, data}.
   */
  adminAnalyticsSessions: (adminApiPrefix, days = 30, limit = 100, identified = 'all') =>
    axios.get(`${adminApiPrefix}/analytics/sessions`, {
      params: { days, limit, identified },
    }),

  /**
   * Facts + ordered event breadcrumb for one session.
   *
   * Events come back oldest-first so the caller can render them as a timeline
   * without re-sorting.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {string} sessionId - The session UUID.
   * @returns {Promise<Object>} Axios response with
   *   {ok, data: {session, events, event_count, truncated}}. 404 if unknown.
   */
  adminAnalyticsSession: (adminApiPrefix, sessionId) =>
    axios.get(`${adminApiPrefix}/analytics/sessions/${sessionId}`),

  /**
   * Fetch paginated audit log entries (super admin only).
   * Append-only viewer — backend exposes only GET for audit_log.
   *
   * @param {string} adminApiPrefix - Full admin API prefix (e.g., '/api/{secret}/admin')
   * @param {Object} [params] - Query parameters: user_id, user_search (ILIKE
   *   across display_name/username/email — 2026-08-26), action, resource_type,
   *   date_from, date_to, result, page, per_page
   * @returns {Promise<{data: {entries: Array, total: number, page: number, per_page: number}}>}
   *   Each entry now also carries user_display_name, user_username, user_email
   *   (nullable) so the UI can render a human label instead of the raw id.
   */
  getAuditLog: (adminApiPrefix, params = {}) =>
    axios.get(`${adminApiPrefix}/audit-log`, { params }),

  // ==================== Super Admin — CRM Ops (Issue #229) ====================

  /**
   * Get the current CRM health snapshot + entity mapping tallies.
   * Uncached admin route — safe for a manual "Refresh" click.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {boolean} [probe=false] - When true, forces a live probe and
   *   invalidates the shared 60s health cache; also writes a
   *   CRM_HEALTH_PROBE audit row.
   * @returns {Promise<{data: {ok:boolean, health:{ok:boolean, detail:string},
   *   mappings:Object, enabled:boolean, probed:boolean}}>}
   */
  crmHealth: (adminApiPrefix, probe = false) =>
    axios.get(`${adminApiPrefix}/crm/health`, {
      params: probe ? { probe: 1 } : {},
    }),

  /**
   * List cogslog rows for the mappings drilldown drawer.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {Object} params - {entity_type, status ('synced'|'unmapped'|'failing'), limit}
   * @returns {Promise<{data: {ok:boolean, rows:Array<Object>}}>}
   */
  crmMappings: (adminApiPrefix, params = {}) =>
    axios.get(`${adminApiPrefix}/crm/mappings`, { params }),

  /**
   * List the most-recent CRM sync failures.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [limit=50] - Max rows (server caps at 200).
   * @returns {Promise<{data: {ok:boolean, rows:Array<Object>}}>}
   */
  crmFailures: (adminApiPrefix, limit = 50) =>
    axios.get(`${adminApiPrefix}/crm/failures`, { params: { limit } }),

  /**
   * List recent CRM operator audit rows (action LIKE 'crm.%').
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [limit=50] - Max rows (server caps at 200).
   * @returns {Promise<{data: {ok:boolean, rows:Array<Object>}}>}
   */
  crmAudit: (adminApiPrefix, limit = 50) =>
    axios.get(`${adminApiPrefix}/crm/audit`, { params: { limit } }),

  /**
   * Sequential idempotent backfill of every live entity type into Twenty.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok:boolean, tally:Object}}>}
   */
  crmBackfill: (adminApiPrefix) =>
    axios.post(`${adminApiPrefix}/crm/backfill`),

  /**
   * Re-sync live entities + re-anonymize deactivated users still mapped.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok:boolean, anonymized:number, resync:Object}}>}
   */
  crmReconcile: (adminApiPrefix) =>
    axios.post(`${adminApiPrefix}/crm/reconcile`),

  /**
   * Delete crm_sync rows whose local entity is missing / inactive AND
   * remove the corresponding record from Twenty.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok:boolean, orphans_removed:number, orphans_failed:number}}>}
   */
  crmCleanupOrphans: (adminApiPrefix) =>
    axios.post(`${adminApiPrefix}/crm/cleanup-orphans`),

  /**
   * Synchronous per-row retry — used by the CRM Ops "Retry" button.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {string} entityType - one of 'user'|'organization'|'business'|'vendor'|'invoice'
   * @param {number} entityId - The cogslog row id.
   * @returns {Promise<{data: {ok:boolean, entity_type:string, entity_id:number,
   *   crm_id:?string, action:'created'|'updated'|'skipped'}}>}
   */
  crmResync: (adminApiPrefix, entityType, entityId) =>
    axios.post(
      `${adminApiPrefix}/crm/resync/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`
    ),

  // ==================== Operations subsystems (GH #229 P4b) ====================

  /**
   * Analytics rollup: last-run info + rows-populated tallies from
   * `analytics_daily`. Read-only, safe to poll from the Operations panel.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok:boolean, latest_day:?string,
   *   latest_updated_at:?string, days_populated_last_30:number,
   *   row_totals:{pageviews:number,sessions:number,users:number,events:number},
   *   coverage_last_7:Array<{day:string, pageviews:number, sessions:number,
   *   users:number, events:number}>}}>}
   */
  opsAnalyticsRollupStatus: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/ops/analytics-rollup/status`),

  /**
   * Analytics rollup: recompute the last N days into `analytics_daily`.
   * Runs inline (typically < 2 s for a few days). Idempotent — the rollup
   * function UPSERTs on `day`.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [daysBack=3] - Days to recompute, clamped [1, 90] server-side.
   * @returns {Promise<{data: {ok:boolean, days_back:number, days_touched:string[]}}>}
   */
  opsAnalyticsRollupRun: (adminApiPrefix, daysBack = 3) =>
    axios.post(`${adminApiPrefix}/ops/analytics-rollup/run`, { days_back: daysBack }),

  /**
   * Feedback → GitHub: mirroring counts + oldest-unmirrored age. Read-only.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok:boolean, enabled:boolean, total:number,
   *   mirrored:number, unmirrored:number, oldest_unmirrored_at:?string,
   *   gh_repo:string}}>}
   */
  opsFeedbackGithubStatus: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/ops/feedback-github/status`),

  /**
   * Feedback → GitHub: mirror up to `limit` un-mirrored rows now. Refuses
   * with 400 `{error:'gh disabled'}` when GITHUB_TOKEN is unset.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} [limit=50] - Max rows to attempt, clamped [1, 500] server-side.
   * @returns {Promise<{data: {ok:boolean, attempted:number, mirrored:number, failed:number}}>}
   */
  opsFeedbackGithubBackfill: (adminApiPrefix, limit = 50) =>
    axios.post(`${adminApiPrefix}/ops/feedback-github/backfill`, { limit }),

  // ==================== System health dashboard ====================

  /**
   * Container-level health. Reads through the Docker socket that is mounted
   * read-only into the backend container (see docker-compose.yml). Cached
   * server-side for 10 seconds so rapid re-clicks don't hammer the socket.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {containers: Array<{name:string, image:string,
   *   status:string, state:string, uptime_seconds:?number,
   *   health_status:?string, restart_count:number,
   *   memory_bytes:?number}>, error?: string}}>}
   */
  getAdminHealthContainers: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/health/containers`),

  /**
   * AWS-managed-service health. Fail-open per service — any single AWS
   * subsystem's failure surfaces as `{error: '...'}` inside its own key
   * rather than a 500 for the whole endpoint. Server-side cached 10s.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {region:string, rds:object, elasticache:object,
   *   s3:object, ses:object, sqs:object}}>}
   */
  getAdminHealthAws: (adminApiPrefix) =>
    axios.get(`${adminApiPrefix}/health/aws`),

  // ==================== Feedback ====================

  /**
   * Submit a new feedback report as multipart/form-data.
   *
   * Sends description, category, optional screenshot, and optional attachments
   * to the rate-limited POST /api/feedback endpoint. The FormData object is
   * built by the caller (FeedbackModal) before calling this method.
   *
   * @param {FormData} formData - Multipart form including text fields and file parts.
   * @returns {Promise<{data: {id: number}}>} ID of the newly created feedback submission.
   *
   * @example
   * const fd = new FormData();
   * fd.append('description', 'Something is broken');
   * fd.append('category', 'bug');
   * const { data } = await api.submitFeedback(fd);
   */
  submitFeedback: (formData) =>
    axios.post('/api/feedback', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /**
   * List feedback submissions from the admin endpoint.
   *
   * Supports optional query parameter filters. Returns paginated results.
   *
   * @param {string} adminApiPrefix - Full admin API prefix (e.g., '/api/admin-<secret>')
   * @param {Object} [params] - Query params: category, status, date_from, date_to,
   *   user_email, org_id, page, per_page
   * @returns {Promise<{data: {items: Array, total: number, page: number, per_page: number}}>}
   *
   * @example
   * const { data } = await api.getFeedback(ADMIN_API_PREFIX, { status: 'new' });
   */
  getFeedback: (adminApiPrefix, params = {}) =>
    axios.get(`${adminApiPrefix}/feedback`, { params }),

  /**
   * Bulk-refresh gh_issue_state for every mirrored feedback row.
   *
   * Cogslog no longer owns feedback triage state (2026-08-26) — GitHub
   * Issues is the source of truth. This endpoint walks every row with
   * gh_issue_number and re-caches the current state from the GH API.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @returns {Promise<{data: {ok:boolean, attempted:number, refreshed:number,
   *   failed:number, closed_now:number, opened_now:number}}>}
   */
  refreshFeedbackGhState: (adminApiPrefix) =>
    axios.post(`${adminApiPrefix}/feedback/refresh-gh-state`),

  /**
   * Return a URL string pointing to the screenshot for a feedback submission.
   *
   * The URL is intended for use as an `<img src>` attribute — the browser
   * fetches it directly. Credentials are included automatically via cookies.
   * Returns a string, not a Promise.
   *
   * @param {string} adminApiPrefix - Full admin API prefix.
   * @param {number} id - Feedback submission ID.
   * @returns {string} URL to the screenshot PNG endpoint.
   *
   * @example
   * <img src={api.getFeedbackScreenshotUrl(ADMIN_API_PREFIX, 42)} alt="Screenshot" />
   */
  getFeedbackScreenshotUrl: (adminApiPrefix, id) =>
    `${adminApiPrefix}/feedback/${id}/screenshot`,

  /**
   * Fetch the feedback screenshot as an authenticated Blob and return an
   * object URL suitable for `<img src>`. Mark UAT 2026-08-28: the previous
   * "getFeedbackScreenshotUrl → <img src=url>" pattern doesn't send the
   * Authorization header (browsers don't attach it on image loads), so
   * every request to the super-admin endpoint 401'd. This helper does the
   * axios fetch (auto-Bearer via the api interceptor) and hands back a
   * blob URL the caller must revoke with URL.revokeObjectURL(url).
   *
   * @param {string} adminApiPrefix - Full admin API prefix (with the URL-path secret).
   * @param {number} id - Feedback submission ID.
   * @returns {Promise<string>} Object URL for the fetched PNG blob.
   */
  fetchFeedbackScreenshotBlobUrl: async (adminApiPrefix, id) => {
    const resp = await axios.get(
      `${adminApiPrefix}/feedback/${id}/screenshot`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(resp.data);
  },

  /**
   * Return a URL string pointing to an extra attachment for a feedback submission.
   *
   * Kept for callers that want the raw URL (e.g. a downloadable link). For
   * `<img src>` use `fetchFeedbackAttachmentBlobUrl` below — see the
   * screenshot helper docstring for why the raw URL doesn't work as an
   * image source on super-admin endpoints.
   */
  getFeedbackAttachmentUrl: (adminApiPrefix, id, n) =>
    `${adminApiPrefix}/feedback/${id}/attachment/${n}`,

  /**
   * Fetch a feedback attachment as an authenticated Blob and return an
   * object URL suitable for `<img src>`. See `fetchFeedbackScreenshotBlobUrl`
   * above for the rationale.
   *
   * @param {string} adminApiPrefix
   * @param {number} id - Feedback submission ID.
   * @param {number} n - Zero-based attachment index.
   * @returns {Promise<string>} Object URL for the fetched blob.
   */
  fetchFeedbackAttachmentBlobUrl: async (adminApiPrefix, id, n) => {
    const resp = await axios.get(
      `${adminApiPrefix}/feedback/${id}/attachment/${n}`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(resp.data);
  },

  /**
   * Fetch the linked cogslog entity + CRM sync state for one feedback row.
   *
   * Powers the "Linked entity & CRM sync" sidebar in the admin feedback
   * viewer (GH #229 Phase 3). Returns `{linked: false}` when the feedback
   * row was submitted without an entity link so the sidebar can render
   * nothing without a follow-up call.
   *
   * @param {string} adminApiPrefix - Full admin API prefix
   *   (e.g. `/api/{secret}/admin`).
   * @param {number} id - Feedback submission id.
   * @returns {Promise<{data: {ok:boolean, linked:boolean,
   *   entity_type?:string, entity_id?:number, display_name?:?string,
   *   crm_object?:?string, crm_id?:?string,
   *   last_success_at?:?string, last_error_at?:?string,
   *   last_error?:?string, error_count?:number,
   *   crm_web_url?:?string}}>}
   *
   * @example
   * const { data } = await api.getFeedbackLinkedEntity(ADMIN_API_PREFIX, 42);
   * if (data.linked) { renderSidebar(data); }
   */
  getFeedbackLinkedEntity: (adminApiPrefix, id) =>
    axios.get(`${adminApiPrefix}/feedback/${id}/linked-entity`),

  /**
   * Fetch the linked GitHub Issue body + comments for one feedback row.
   *
   * Powers the "GitHub Issue #N" panel inside the super-admin Feedback
   * row-expand (2026-08-29). Backed by a 10-minute in-process TTL
   * cache on the server so re-opening the same row doesn't burn the
   * 5000/hr GH REST quota.
   *
   * Response is always HTTP 200 on both success and graceful failure
   * (the frontend switches on `ok` + `error` to pick the render
   * branch); the only hard error surfaces are 404 for a missing
   * feedback row and 404 `{error: 'no_linked_issue'}` when the row
   * exists but was never mirrored to GitHub.
   *
   * @param {string} adminApiPrefix - Full admin API prefix
   *   (e.g. `/api/{secret}/admin`).
   * @param {number} feedbackId - Feedback submission id.
   * @returns {Promise<{data: {ok: boolean, issue?: Object,
   *   comments?: Object[], error?: string, issue_number?: number,
   *   status?: number, comments_error?: string}}>}
   *
   * @example
   * const { data } = await api.fetchFeedbackGithubThread(ADMIN_API_PREFIX, 42);
   * if (data.ok) { render(data.issue, data.comments); }
   */
  fetchFeedbackGithubThread: (adminApiPrefix, feedbackId) =>
    axios.get(`${adminApiPrefix}/feedback/${feedbackId}/github-thread`),

  // ==================== Auth / Registration ====================

  /**
   * Register a new user account with email verification.
   * @param {Object} data - { first_name, last_name, email, password }
   * @returns {Promise<{data: {message: string}}>} Success message
   */
  register: (data) => axios.post(`${API_BASE}/auth/register`, data),

  /**
   * Verify a user's email address using a token from the verification email.
   * @param {Object} data - { token: string }
   * @returns {Promise<{data: {message: string}}>} Verification result
   */
  verifyEmail: (data) => axios.post(`${API_BASE}/auth/verify-email`, data),

  /**
   * Resend the email verification link to a user's email address.
   * @param {Object} data - { email: string }
   * @returns {Promise<{data: {message: string}}>} Resend result
   */
  resendVerification: (data) => axios.post(`${API_BASE}/auth/resend-verification`, data),

  /**
   * Log in with email and password credentials.
   * @param {Object} data - { email: string, password: string }
   * @returns {Promise<{data: {access_token: string, refresh_token: string}}>} Token pair
   */
  login: (data) => axios.post(`${API_BASE}/auth/login`, data),

  /**
   * Refresh an expired access token using a refresh token.
   * @param {Object} data - { refresh_token: string }
   * @returns {Promise<{data: {access_token: string, refresh_token: string}}>} New token pair
   */
  refreshToken: (data) => axios.post(`${API_BASE}/auth/refresh`, data),

  /**
   * Request a password reset email for the given email address.
   * @param {Object} data - { email: string }
   * @returns {Promise<{data: {message: string}}>} Success message
   */
  forgotPassword: (data) => axios.post(`${API_BASE}/auth/forgot-password`, data),

  /**
   * Reset a user's password using a reset token.
   * @param {Object} data - { token: string, password: string, confirm_password: string }
   * @returns {Promise<{data: {message: string}}>} Success message
   */
  resetPassword: (data) => axios.post(`${API_BASE}/auth/reset-password`, data),

  /**
   * Check whether registration requires an access code.
   * @returns {Promise<{data: {requires_code: boolean}}>} Registration status
   */
  getRegistrationStatus: () => axios.get(`${API_BASE}/auth/registration-status`),

  /**
   * Validate a registration access code.
   * @param {Object} data - { code: string }
   * @returns {Promise<{data: {token: string}}>} Registration token on success
   */
  validateRegistrationCode: (data) => axios.post(`${API_BASE}/auth/validate-registration-code`, data),

  // ==================== Invitations ====================

  /**
   * Get invitation details by token (public endpoint).
   * @param {string} token - The invitation token UUID
   * @returns {Promise<{data: {organization_name, inviter_name, role, email, requires_registration}}>}
   */
  getInvitationDetails: (token) =>
    axios.get(`${API_BASE}/auth/accept-invitation`, { params: { token } }),

  /**
   * Accept an organization invitation.
   * @param {Object} data - {token: string} for existing users, or full registration data for new users
   * @returns {Promise<{data: {message: string}}>} Acceptance result
   */
  acceptInvitation: (data) => axios.post(`${API_BASE}/auth/accept-invitation`, data),

  /**
   * Send an invitation to join an organization.
   * @param {number} orgId - Organization ID
   * @param {Object} data - {email: string, role?: string}
   * @returns {Promise<{data: Object}>} Created invitation
   */
  sendOrgInvitation: (orgId, data) =>
    axios.post(`${API_BASE}/organizations/${orgId}/invite`, data),

  /**
   * List pending invitations for an organization.
   * @param {number} orgId - Organization ID
   * @returns {Promise<{data: Array}>} List of pending invitations
   */
  getOrgInvitations: (orgId) =>
    axios.get(`${API_BASE}/organizations/${orgId}/invitations`),

  /**
   * Revoke a pending organization invitation.
   * @param {number} orgId - Organization ID
   * @param {number} invitationId - Invitation record ID
   * @returns {Promise<{data: {message: string}}>} Revocation result
   */
  revokeOrgInvitation: (orgId, invitationId) =>
    axios.delete(`${API_BASE}/organizations/${orgId}/invitations/${invitationId}`),

  // ==================== GDPR Consent ====================

  /**
   * Record the user's re-acceptance of updated Terms of Service.
   *
   * Called by TermsReacceptanceModal when the user clicks the accept button.
   * On success, AuthContext re-fetches /api/auth/me to clear the flag.
   *
   * @param {string} termsVersion - ISO date string of the terms version being accepted
   * @returns {Promise<{data: {message: string}}>} Acceptance confirmation
   */
  acceptTerms: (termsVersion) =>
    axios.post(`${API_BASE}/auth/accept-terms`, { terms_version: termsVersion }),

  /**
   * Detect whether the user's request originates from the EU/UK region.
   *
   * Uses the CloudFront-Viewer-Country header injected by AWS CloudFront.
   * Returns {is_eu: false, country_code: ''} when accessed without CloudFront.
   * No authentication required — called before login for cookie banner decision.
   *
   * @returns {Promise<{data: {is_eu: boolean, country_code: string}}>} Geo detection result
   */
  geoCheck: () =>
    axios.get(`${API_BASE}/auth/geo`),

  // ==================== Cookie Preferences ====================

  /**
   * Get the authenticated user's cookie consent preferences from the database.
   *
   * Returns {necessary: true, analytics: false, marketing: false} by default
   * when the user has no saved preferences.
   *
   * @returns {Promise<{data: {necessary: boolean, analytics: boolean, marketing: boolean}}>}
   */
  getCookiePreferences: () =>
    axios.get(`${API_BASE}/cookie-preferences`),

  /**
   * Save the authenticated user's cookie consent preferences to the database.
   *
   * The necessary field is always forced true on the backend regardless of
   * what is passed here.
   *
   * @param {{analytics: boolean, marketing: boolean}} prefs - Preferences to save
   * @returns {Promise<{data: {message: string}}>} Confirmation message
   */
  saveCookiePreferences: (prefs) =>
    axios.post(`${API_BASE}/cookie-preferences`, prefs),

  // ==================== Help (in-app documentation, DOC-1) ====================

  /**
   * List the help documents the current user is allowed to access.
   * Returns `{ docs: [{ slug, title, requires_super_admin }] }`.
   *
   * The super-admin guide is omitted for non-admin callers (backend gate).
   *
   * @returns {Promise<{data: {docs: Array<{slug: string, title: string, requires_super_admin: boolean}>}}>}
   */
  helpIndex: () => axios.get(`${API_BASE}/help`),

  /**
   * Fetch the raw markdown source of a help document by slug.
   *
   * @param {string} slug - One of: 'user-manual', 'superadmin-guide'
   * @returns {Promise<{data: string}>} The markdown source as a string
   */
  helpDoc: (slug) =>
    axios.get(`${API_BASE}/help/${encodeURIComponent(slug)}`, {
      responseType: 'text',
      transformResponse: [(d) => d],
    }),
};

export default api;
